import { gsap } from 'gsap';
import { useLocation } from '@reach/router';
import { type RefObject, useEffect, useRef, SyntheticEvent } from 'react';
import { LOCAL_STORAGE_KEY_HOME_ANIMATION_VISIBLE } from '../Atoms/Icons/Logo';
import { graphql, useStaticQuery } from 'gatsby';
import generateWatchProjectImage from '../../utils/generateWatchProjectImage';
import Link from '../Atoms/Link';

// const firstImage = {
//   slug: null,
//   type: null,
//   title: null,
//   middleImage: '/loading-5.webp',
//   middleAltText: 'watch',
// }

// const lastImage = {
//   slug: null,
//   type: null,
//   title: null,
//   middleImage: '/loading-1.webp',
//   middleAltText: 'watch',
// }

const calcWinsize = () => ({ width: window.innerWidth, height: window.innerHeight });
const getRandomInteger = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1) + min);

const getSecondImageY = (item: HTMLElement, winsize: { width: number; height: number }) => winsize.width < 1024 ? winsize.height / 4 - item.getBoundingClientRect().height / 2 : winsize.height / 2 - item.getBoundingClientRect().top / 1.5

const InitialLoadingAnimation = ({ textRef, sellBuyDecisionRef }: { textRef: RefObject<HTMLDivElement>, sellBuyDecisionRef: RefObject<HTMLDivElement> }) => {
  const galleryRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const q = gsap.utils.selector(galleryRef);
  const latest3Images = useStaticQuery(graphql`
    query Latest3Magazines {
      allMagazine(limit: 5, sort: {createdAt: DESC}) {
        nodes {
          slug
          type
          middleAltText
          middleImage
          title
        }
      }
    }
  `);
  const { pathname } = useLocation();

  const newImages = latest3Images.allMagazine.nodes.map((imageItem) => ({ ...imageItem, middleImage: generateWatchProjectImage(imageItem.middleImage, imageItem.middleAltText) }))
  newImages.unshift(newImages.pop());
  const images = newImages;

  useEffect(() => {
    const winsize = calcWinsize();

    images.forEach((imageItem, pos) => {
      const item = q(`a[data-src="${imageItem.middleImage}"]`)[0];
      gsap.set(item, { zIndex: images.length - 1 - pos });
    })

    gsap.to(galleryRef.current, {
      duration: 1,
      ease: 'expo',
      startAt: { y: '4%' },
      y: '0%'
    })

    gsap.to(textRef.current, {
      duration: 0.1,
      ease: 'expo',
      visibility: 'visible',
      startAt: { y: '-100%' },
      y: '0%'
    })

    requestAnimationFrame(() => {
      gsap.to(textRef.current, {
        duration: 1,
        opacity: 0,
        ease: 'expo',
        startAt: { y: '-100%' },
        y: '0%'
      })

      const [zerothImage, firstImage, secondImage, thirdImage, fourthImage] = images.map((imageItem) => q(`a[data-src="${imageItem.middleImage}"]`)[0]);
      const timeline = gsap.timeline();

      const isAnimationVisible = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_HOME_ANIMATION_VISIBLE));
      if (pathname !== '/') {
        if (isAnimationVisible) {
          timeline.delay(1.5);
        } else {
          timeline.delay(0);
        }
      }

      timeline.set(logoRef.current, {
        x: winsize.width / 2 - (logoRef?.current?.getBoundingClientRect().left || 0) - (logoRef?.current?.getBoundingClientRect().width || 0) / 2,
        y: winsize.height / 2 - (logoRef?.current?.getBoundingClientRect().top || 0) - (logoRef?.current?.getBoundingClientRect().height || 0) / 2,
      }, 0)

      // center the images
      images.reverse().forEach((imageItem, pos) => {
        const item = q(`a[data-src="${imageItem.middleImage}"]`)[0];

        const y = winsize.height / 2 - item.getBoundingClientRect().top - item.getBoundingClientRect().height / 2

        timeline.set(item, {
          x: winsize.width / 2 - item.getBoundingClientRect().left - item.getBoundingClientRect().width / 2,
          y,
          scale: winsize.width > 1024 ? 0.75 : 1.25,
          rotation: getRandomInteger(-10, 10),
          opacity: 1,
          delay: 0.4 * pos
        }, 0);
      })

      timeline
        .addLabel('startAnimation', '+=0.1')
        .add(() => { }, 'startAnimation')
        .to(logoRef.current, {
          opacity: 0,
          duration: 1,
          ease: 'expo',
        })
        // center images
        .to([zerothImage, firstImage, secondImage, thirdImage, fourthImage], {
          duration: 0.75,
          ease: 'expo',
          scale: 1,
          rotation: 0,
          opacity: 1,
        }, 'startAnimation')

        // remove first image
        .to(zerothImage, {
          x: winsize.width < 1024 ? -5000 : 0 - zerothImage.getBoundingClientRect().width / 2 - 64,
          y: winsize.width / 2 - zerothImage.getBoundingClientRect().left - zerothImage.getBoundingClientRect().width * 1.5 - 12 - (winsize.width > 1024 ? 36 : 0),
          zIndex: 0,
        }, 'startAnimation')

        // move middle image a bit higher
        .to(secondImage, {
          x: winsize.width / 2 - secondImage.getBoundingClientRect().left - secondImage.getBoundingClientRect().width / 2,
          y: getSecondImageY(secondImage, winsize),
          zIndex: 9
        }, 'startAnimation')

        // remove last image
        .to(fourthImage, {
          x: winsize.width < 1024 ? 5000 : winsize.width / 2 - fourthImage.getBoundingClientRect().left + fourthImage.getBoundingClientRect().width,
          y: winsize.width / 2 - fourthImage.getBoundingClientRect().left - fourthImage.getBoundingClientRect().width * 1.5 - 12 - (winsize.width > 1024 ? 36 : 0)
        }, 'startAnimation')

      timeline
        .to(firstImage, {
          y: 0,
          x: winsize.width / 2 - firstImage.getBoundingClientRect().left - firstImage.getBoundingClientRect().width * 1.5 - 12 - (winsize.width > 1024 ? 36 : 0),
        }, 'startAnimation')
        .to(thirdImage, {
          x: winsize.width / 2 - thirdImage.getBoundingClientRect().left + thirdImage.getBoundingClientRect().width / 2 + 12 + (winsize.width > 1024 ? 36 : 0),
          y: 0,
        }, 'startAnimation')

        // hide other images
        .to([zerothImage, fourthImage], {
          opacity: 0,
          delay: 0.5
        }, 'startAnimation+=0.25')

      // display text
      timeline.to(textRef.current, {
        duration: 1.5,
        ease: 'expo',
        startAt: {
          y: '50%'
        },
        y: '0%',
        opacity: 1,
        delay: 0.5
      }, 'startAnimation+=0.1');

      timeline.to(sellBuyDecisionRef.current, {
        duration: 1,
        ease: 'expo',
        startAt: {
          y: '50%'
        },
        y: '0%',
        opacity: 1,
        delay: 0.75,
      }, 'startAnimation+=0.1')
    })
  }, [])

  return (
    <div
      ref={wrapperRef}
      className="h-112 sm:h-700 md:h-550 relative bg-white"
      data-scroll-section
    >
      <img
        src="/logo-white.svg"
        alt="logo-white"
        ref={logoRef}
        loading="lazy"
        className="z-10 h-40 w-40 lg:h-64 lg:w-64 absolute left-1/2 -translate-x-1/2 opacity-1 top-1/2 -translate-y-1/2"
      />
      <div className="lg:max-w-screen-2xl w-full mx-auto pt-16 relative h-full md:h-112 lg:h-700" ref={galleryRef}>
        {images.map((image, index) => (
          <LoadingAnimationImage
            image={image}
            key={`loading-animation-image-${index}`}
          />
        ))}
      </div>
    </div >
  )
}

type LoadingAnimationImageProps = {
  image: {
    slug: string
    type: string
    title: string
    middleImage: string
  }
}

const LoadingAnimationImage = ({ image }: LoadingAnimationImageProps) => {

  const handleError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = image.middleImage;
  }

  return (
    <Link
      className='block min-w-40 w-1/3 lg:w-112 lg:h-600l opacity-0 absolute'
      to={image.slug ? `/${image.type}/${image.slug}` : '/magazine'}
      data-src={image.middleImage}
    >
      <div
        className="flex h-full w-full absolute top-0 left-0 p-8 transition-all duration-500 ease-in-out hover:bg-black hover:bg-opacity-60 text-transparent hover:text-primary text-2xl lg:text-7xl font-cirka"
      >
        <div className="mt-auto">{image.title}</div>
      </div>
      <picture
        className="object-cover overflow-hidden min-w-40 w-1/3 lg:w-112 lg:h-600 will-change-transform"
      >
        {/*
          // TODO: figure out better way to manage croppings
          // TODO: handle errors
          // TODO: fix styling issues
          // onError={() => {}}
        */}
        <source
          media="(max-width: 512px)"
          srcSet={encodeURI(image.middleImage.replace('.webp', '-280.webp'))}

        />
        <source
          media="(max-width: 1024px)"
          srcSet={encodeURI(image.middleImage.replace('.webp', '-448.webp'))}
        />
        <source
          media="(min-width: 1024px)"
          srcSet={encodeURI(image.middleImage.replace('.webp', '-600.webp'))}
        />
        <img
          src={image.middleImage}
          alt="watch"
          width={448}
          height={600}
          // eslint-disable-next-line react/no-unknown-property
          fetchPriority='high'
          onError={handleError}
        />
      </picture>
    </Link>
  )
}


export default InitialLoadingAnimation;
